import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Alert,
  Spinner,
  Card,
  Badge,
  ButtonGroup,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// Contexts
import UserContext from "../../contexts/UserContext";
import NotificationsContext from "../../contexts/NotificationsContext";

// Services
import SellerStoreServices from "./services/SellerStoreServices";

// Styles
import "./marketplace.css";
import "./ShopList.modern.css";

const ShopList = () => {
  const navigate = useNavigate();
  const { getUserPlan } = useContext(UserContext);
  const { setNotify } = useContext(NotificationsContext);

  // States
  const [sellerData, setSellerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [noProfile, setNoProfile] = useState(false);

  const currentPlan = getUserPlan();
  const hasAccess = SellerStoreServices.hasPermission(currentPlan);

  // Load seller data
  const loadShops = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNoProfile(false);

      const response = await SellerStoreServices.listStores();
      const data = response.data;

      // Verificar se o usuário não possui perfil de vendedor
      if (data.has_profile === false) {
        setNoProfile(true);
        setSellerData(null);
        setError(null);
        return;
      }

      // A nova API retorna um objeto com has_store, store, profile, etc.
      setSellerData(data);
    } catch (error) {
      console.error("Erro ao carregar dados do seller:", error);

      // Verificar se o erro indica que não há perfil criado
      if (error.response?.data?.has_profile === false) {
        setNoProfile(true);
        setSellerData(null);
        setError(null);
      } else if (SellerStoreServices.isNoStoresFound(error)) {
        setSellerData({ has_store: false, can_create_store: true }); // Permitir criação
        setError(null); // Não mostrar erro
      } else {
        // Para outros erros, mostrar mensagem de erro
        const errorMessage = SellerStoreServices.getErrorMessage(error);
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (hasAccess) {
      loadShops();
    }
  }, [hasAccess, loadShops]);

  // Handle refresh
  const handleRefresh = () => {
    loadShops();
  };

  // Handle activate shop
  const handleActivateShop = async () => {
    if (!sellerData?.store) return;

    const result = await Swal.fire({
      title: "Ativar Loja",
      text: "Deseja ativar esta loja no marketplace?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, ativar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#28a745",
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(sellerData.store.store_slug);
      await SellerStoreServices.patchStore(sellerData.store.store_slug, { is_active: true });

      setNotify({
        title: "Sucesso!",
        message: "Loja ativada com sucesso!",
        type: "success",
        visible: true,
      });

      loadShops();
    } catch (error) {
      console.error("Erro ao ativar loja:", error);
      const errorMessage = SellerStoreServices.getErrorMessage(error);

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle deactivate shop
  const handleDeactivateShop = async () => {
    if (!sellerData?.store) return;

    const result = await Swal.fire({
      title: "Desativar Loja",
      text: "Deseja desativar temporariamente esta loja?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, desativar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ffc107",
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(sellerData.store.store_slug);
      await SellerStoreServices.patchStore(sellerData.store.store_slug, { is_active: false });

      setNotify({
        title: "Sucesso!",
        message: "Loja desativada com sucesso!",
        type: "success",
        visible: true,
      });

      loadShops();
    } catch (error) {
      console.error("Erro ao desativar loja:", error);
      const errorMessage = SellerStoreServices.getErrorMessage(error);

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle remove shop
  const handleRemoveShop = async () => {
    if (!sellerData?.store) return;

    const result = await Swal.fire({
      title: "Remover Loja",
      html: `
        <p>Deseja remover permanentemente esta loja?</p>
        <p class="small text-muted">Esta ação não pode ser desfeita.</p>
      `,
      icon: "error",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(sellerData.store.store_slug);
      await SellerStoreServices.deactivateStore(sellerData.store.store_slug);

      setNotify({
        title: "Sucesso!",
        message: "Loja removida com sucesso!",
        type: "success",
        visible: true,
      });

      loadShops();
    } catch (error) {
      console.error("Erro ao remover loja:", error);
      const errorMessage = SellerStoreServices.getErrorMessage(error);

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Access control
  if (!hasAccess) {
    return (
      <div className="msl-marketplace-shops-page">
        <div className="msl-access-denied">
          <div className="msl-access-icon">
            <i className="las la-lock" />
          </div>
          <h3 className="msl-access-title">Recurso Indisponível</h3>
          <div className="msl-access-content">
            <p>
              O MercadoShop está disponível apenas para os planos{" "}
              <strong>Pro</strong> e <strong>Business</strong>.
            </p>
            <p>
              Seu plano atual:{" "}
              <strong>{currentPlan === 3 ? "Business" : "Starter"}</strong>
            </p>
            <p>
              Para acessar este recurso, considere fazer upgrade para o plano
              Pro.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="msl-marketplace-shops-page">
      {/* Error Alert */}
      {error && (
        <div className="msl-error-alert">
          <button className="msl-error-close" onClick={() => setError(null)}>
            <i className="las la-times" />
          </button>
          <div className="msl-error-header">
            <i className="las la-exclamation-triangle msl-error-icon" />
            <h4 className="msl-error-title">Erro</h4>
          </div>
          <p className="msl-error-message">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="msl-loading-card">
          <div className="msl-loading-content">
            <div className="msl-loading-spinner" />
            <h3 className="msl-loading-title">Carregando loja...</h3>
          </div>
        </div>
      )}

      {/* Store Display - When user has a store */}
      {!loading && sellerData?.has_store && sellerData?.store && (
        <div className="msl-shops-grid">
          <div className="msl-shop-card">
            {/* Por enquando hardcoded, depois mudar para o thumbor */}
            {sellerData.store.banner_image ? (
              <img
                src={`https://mercadoagentes.s3.amazonaws.com/${sellerData.store.banner_image}`}
                alt="Banner da loja"
                className="msl-shop-banner"
              />
            ) : (
              <div className="msl-shop-banner placeholder">
                <i className="las la-store" />
              </div>
            )}

            <div className="msl-shop-content">
              <div className="msl-shop-header">
                <div className="msl-shop-info">
                  <h3>
                    {sellerData.store.store_name ||
                      sellerData.profile?.display_name ||
                      "Loja"}
                  </h3>
                  <p className="msl-shop-subtitle">
                    <i className="las la-user" />
                    {sellerData.user?.full_name ||
                      sellerData.profile?.display_name ||
                      "Proprietário"}
                    {sellerData.profile?.bio && (
                      <>
                        <span> • </span>
                        {sellerData.profile.bio.substring(0, 50)}...
                      </>
                    )}
                  </p>
                </div>
                <div
                  className={`msl-shop-status ${
                    sellerData.store.is_active ? "active" : "inactive"
                  }`}
                >
                  <i
                    className={`las la-${
                      sellerData.store.is_active
                        ? "check-circle"
                        : "pause-circle"
                    }`}
                  />
                  {sellerData.store.is_active ? "Ativa" : "Inativa"}
                </div>
              </div>

              {/* Statistics */}
              <div className="msl-shop-stats">
                <div className="msl-stat-item">
                  <div className="msl-stat-value text-primary">
                    {sellerData.stats?.total_agents || 0}
                  </div>
                  <div className="msl-stat-label">Agentes</div>
                </div>
                <div className="msl-stat-item">
                  <div className="msl-stat-value text-success">
                    {sellerData.stats?.published_agents || 0}
                  </div>
                  <div className="msl-stat-label">Publicados</div>
                </div>
                <div className="msl-stat-item">
                  <div className="msl-stat-value text-warning">
                    ⭐ {sellerData.store.average_rating || "0.0"}
                  </div>
                  <div className="msl-stat-label">Avaliação</div>
                </div>
              </div>

              {/* Badges */}
              {sellerData.store.badges &&
                sellerData.store.badges.length > 0 && (
                  <div className="msl-shop-badges">
                    {sellerData.store.badges.map((badge, index) => (
                      <span key={index} className="msl-badge">
                        {badge}
                      </span>
                    ))}
                  </div>
                )}

              {/* Contact Information */}
              {(sellerData.store.contact_info?.email ||
                sellerData.store.contact_info?.phone) && (
                <div className="msl-shop-section">
                  <h6 className="msl-section-title">Contato</h6>
                  <div className="msl-contact-info">
                    {sellerData.store.contact_info?.email && (
                      <p className="msl-contact-item">
                        <i className="las la-envelope" />
                        {sellerData.store.contact_info.email}
                      </p>
                    )}
                    {sellerData.store.contact_info?.phone && (
                      <p className="msl-contact-item">
                        <i className="las la-phone" />
                        {sellerData.store.contact_info.phone}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Social Links */}
              {Object.values(sellerData.store.social_links || {}).some(
                (link) => link
              ) && (
                <div className="msl-shop-section">
                  <h6 className="msl-section-title">Redes Sociais</h6>
                  <div className="msl-social-links">
                    {sellerData.store.social_links?.website && (
                      <a
                        href={sellerData.store.social_links.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="msl-social-link"
                        title="Website"
                      >
                        <i className="las la-globe" />
                      </a>
                    )}
                    {sellerData.store.social_links?.facebook && (
                      <a
                        href={sellerData.store.social_links.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="msl-social-link"
                        title="Facebook"
                      >
                        <i className="lab la-facebook" />
                      </a>
                    )}
                    {sellerData.store.social_links?.instagram && (
                      <a
                        href={sellerData.store.social_links.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="msl-social-link"
                        title="Instagram"
                      >
                        <i className="lab la-instagram" />
                      </a>
                    )}
                    {sellerData.store.social_links?.linkedin && (
                      <a
                        href={sellerData.store.social_links.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="msl-social-link"
                        title="LinkedIn"
                      >
                        <i className="lab la-linkedin" />
                      </a>
                    )}
                    {sellerData.store.social_links?.twitter && (
                      <a
                        href={sellerData.store.social_links.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="msl-social-link"
                        title="Twitter"
                      >
                        <i className="lab la-twitter" />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Shop Actions */}
              <div className="msl-shop-actions">
                <Link
                  to={`/marketplace/shop/edit/${sellerData.store.store_slug}`}
                  className="msl-shop-action primary"
                >
                  <i className="las la-edit" />
                  Editar
                </Link>

                {sellerData.store.is_active ? (
                  <button
                    className="msl-shop-action warning"
                    onClick={handleDeactivateShop}
                    disabled={actionLoading === sellerData.store.store_slug}
                  >
                    {actionLoading === sellerData.store.store_slug ? (
                      <div className="msl-action-spinner" />
                    ) : (
                      <>
                        <i className="las la-pause" />
                        Desativar
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    className="msl-shop-action success"
                    onClick={handleActivateShop}
                    disabled={actionLoading === sellerData.store.store_slug}
                  >
                    {actionLoading === sellerData.store.store_slug ? (
                      <div className="msl-action-spinner" />
                    ) : (
                      <>
                        <i className="las la-play" />
                        Ativar
                      </>
                    )}
                  </button>
                )}

                <button
                  className="msl-shop-action danger"
                  onClick={handleRemoveShop}
                  disabled={actionLoading === sellerData.store.store_slug}
                  title="Remover loja"
                >
                  {actionLoading === sellerData.store.store_slug ? (
                    <div className="msl-action-spinner" />
                  ) : (
                    <i className="las la-trash" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Profile Screen - When user doesn't have a seller profile */}
      {!loading && noProfile && !error && (
        <div className="msl-no-profile-screen">
          <div className="msl-no-profile-hero">
            <div className="msl-no-profile-icon">
              <i className="las la-user-plus" />
            </div>
            <h2 className="msl-no-profile-title">
              Perfil de Vendedor Necessário
            </h2>
            <p className="msl-no-profile-subtitle">
              Para criar sua loja no marketplace, você precisa primeiro criar seu perfil de vendedor.
            </p>
          </div>

          <div className="msl-no-profile-content">
            <div className="msl-profile-benefits">
              <div className="msl-benefit-item">
                <i className="las la-check-circle" />
                <span>Configure suas informações pessoais e comerciais</span>
              </div>
              <div className="msl-benefit-item">
                <i className="las la-check-circle" />
                <span>Adicione dados de contato e redes sociais</span>
              </div>
              <div className="msl-benefit-item">
                <i className="las la-check-circle" />
                <span>Personalize sua apresentação no marketplace</span>
              </div>
            </div>

            <div className="msl-no-profile-actions">
              <Link to="/marketplace/seller-profile" className="msl-primary-cta">
                <i className="las la-user-edit" />
                Criar Perfil de Vendedor
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Screen for MercadoShop - When user doesn't have a store */}
      {!loading &&
        !noProfile &&
        (!sellerData?.has_store || sellerData?.has_store === false) &&
        !error && (
          <div className="msl-welcome-screen">
            <div className="msl-welcome-hero">
              <div className="msl-welcome-icon">
                <i className="las la-store-alt" />
              </div>

              <h2 className="msl-welcome-subtitle">
                Crie sua loja virtual e venda seus agentes no 1º marketplace de
                Agentes de IA do Brasil!
              </h2>
            </div>

            <div className="msl-welcome-features">
              <div className="msl-feature-card">
                <div className="msl-feature-icon">
                  <i className="las la-rocket" />
                </div>
                <h3>Lançamento Rápido</h3>
                <p>
                  Configure sua loja em minutos e comece a vender imediatamente
                </p>
              </div>

              <div className="msl-feature-card">
                <div className="msl-feature-icon">
                  <i className="las la-chart-line" />
                </div>
                <h3>Analytics Avançados</h3>
                <p>Acompanhe vendas, métricas e performance em tempo real</p>
              </div>

              <div className="msl-feature-card">
                <div className="msl-feature-icon">
                  <i className="las la-users" />
                </div>
                <h3>Alcance Nacional</h3>
                <p>Conecte-se com milhares de clientes em todo o Brasil</p>
              </div>
            </div>

            <div className="msl-welcome-actions">
              <Link to="/marketplace/shop/create" className="msl-primary-cta">
                <i className="las la-plus" />
                Criar Minha Loja Agora
              </Link>
            </div>

            {/* <div className="msl-welcome-benefits">
            <div className="msl-benefit-item">
              <i className="las la-check-circle" />
              <span>Sem taxas de configuração</span>
            </div>
            <div className="msl-benefit-item">
              <i className="las la-check-circle" />
              <span>Suporte técnico incluso</span>
            </div>
            <div className="msl-benefit-item">
              <i className="las la-check-circle" />
              <span>Marketing integrado</span>
            </div>
          </div> */}
          </div>
        )}
    </div>
  );
};

export default ShopList;
